import axios from 'axios';
import * as Sentry from '@sentry/react';

import localStorageUtil from 'src/utils/localStorageUtil';
import { makeSignatureKey } from 'src/utils/accessApiKeyUtil';
import { storageConstants } from 'src/utils/constants/storageConstants';

import { handleApiError } from './handleApiError';

const ACCESS_KEY = import.meta.env.VITE_X_CLIENT_ACCESS_KEY;
const SIGNATURE_KEY = import.meta.env.VITE_X_CLIENT_SIGNATURE_KEY;

// Axios 인스턴스 생성
const axiosInstance = axios.create({
  baseURL: localStorageUtil.getApiURl() ?? '', // .env 파일에 API 기본 URL 설정
  timeout: 10000, // 요청 타임아웃 시간 설정
  headers: {
    'Content-Type': 'application/json',
    'X-ClientAccessKey': ACCESS_KEY,
  },
});

// 요청 인터셉터 설정
axiosInstance.interceptors.request.use(
  (config) => {
    // X-ClientSignature 헤더 추가
    config.headers['X-ClientSignature'] = makeSignatureKey(SIGNATURE_KEY);
    const token = localStorageUtil.getToken() ?? '';
    config.headers.authToken = token;
    return config;
  },
  (error) => Promise.reject(error)
);

// 응답 인터셉터 설정
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // x-clientSignature 키 만료 에러 감지
    const isSignatureExpiredError =
      error?.response?.status === 401 &&
      error?.response?.data?.meta?.errMsg?.includes('API 접근 암호화 키');

    if (isSignatureExpiredError) {
      const userParams = localStorage.getItem(storageConstants.userParams);
      const userData = userParams ? JSON.parse(userParams) : null;
      const brandCode = localStorage.getItem(storageConstants.brandCode);

      Sentry.captureMessage('[학습자] X-ClientSignature 키 만료 에러', {
        level: 'error',
        tags: {
          errorType: 'signature_expired',
          tenantId: brandCode ?? 'BrandCode 없음',
        },
        extra: {
          // 사용자 정보
          userId: userData?.userId ?? 'UserId 없음',
          tenantId: brandCode ?? 'BrandCode 없음',

          // 요청 정보
          requestUrl: error?.response?.config?.url,
          requestMethod: error?.response?.config?.method,

          // 시간 정보 (에러 발생 시점)
          errorTime: new Date().toISOString(),

          // 사용된 시그니처 (백엔드 로그와 대조용)
          usedSignature: error?.response?.config?.headers?.['X-ClientSignature'] ?? '시그니처 없음',

          // 에러 정보
          errorMessage: error?.response?.data?.meta?.errTranslate,
          errorCode: error?.response?.data?.meta?.errCode,
        },
      });
    }

    // API 에러 핸들링
    const handled = handleApiError(error);

    if (!handled) {
      // 에러를 처리하지 않은 경우에만 다시 던짐
      return Promise.reject(error);
    }

    // 에러가 처리된 경우 ErrorBoundary로 전달하지 않음. 여기서 완료된 프로미스를 반환하여 중단
    return Promise.resolve(null);
  }
);

export const setAuthInHeader = (token: string) => {
  localStorage.setItem('token', token);
  axiosInstance.defaults.headers.common.authToken = token || null;
};
// This function is created to handle file downloads. It requires an access key for downloading.
export const fileDownload = (url: string) =>
  axiosInstance.request({
    method: 'get',
    url,
    responseType: 'arraybuffer',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  });
// baseURL 업데이트 함수
export const updateAxiosBaseURL = (apiUrl: string) => {
  axiosInstance.defaults.baseURL = apiUrl;
};

export default axiosInstance;
